import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Download, Crown, Check } from "lucide-react";
import { useLang } from "@/i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TXT = {
  es: {
    overline: "Contenido Premium",
    title: "Tesis completa · Documento oficial UCV",
    desc: "La investigación íntegra (todos los capítulos, anexos y referencias) disponible para suscriptores.",
    benefits: ["Descarga inmediata del documento completo", "Acceso a futuros contenidos premium", "Apoya directamente al autor", "Cancela cuando quieras"],
    subscribe: "Suscribirme — $9.99/mes",
    subscribing: "Abriendo pago seguro…",
    unlocked: "Suscripción premium activa",
    download: "Descargar tesis completa",
    downloading: "Descargando…",
    testNote: "Modo prueba: usa la tarjeta 4242 4242 4242 4242, cualquier fecha y CVC.",
    okDownload: "Descarga iniciada. Buena lectura.",
    errCheckout: "No se pudo abrir el pago. Inténtalo de nuevo.",
    errDownload: "Acceso premium no válido o expirado.",
  },
  en: {
    overline: "Premium Content",
    title: "Full thesis · Official UCV document",
    desc: "The complete research (all chapters, annexes and references) available to subscribers.",
    benefits: ["Instant download of the full document", "Access to future premium content", "Directly support the author", "Cancel anytime"],
    subscribe: "Subscribe — $9.99/mo",
    subscribing: "Opening secure checkout…",
    unlocked: "Premium subscription active",
    download: "Download full thesis",
    downloading: "Downloading…",
    testNote: "Test mode: use card 4242 4242 4242 4242, any date and CVC.",
    okDownload: "Download started. Enjoy the read.",
    errCheckout: "Could not open checkout. Try again.",
    errDownload: "Premium access invalid or expired.",
  },
  pt: {
    overline: "Conteúdo Premium",
    title: "Tese completa · Documento oficial UCV",
    desc: "A pesquisa íntegra (todos os capítulos, anexos e referências) disponível para assinantes.",
    benefits: ["Download imediato do documento completo", "Acesso a futuros conteúdos premium", "Apoie diretamente o autor", "Cancele quando quiser"],
    subscribe: "Assinar — $9.99/mês",
    subscribing: "Abrindo pagamento seguro…",
    unlocked: "Assinatura premium ativa",
    download: "Baixar tese completa",
    downloading: "Baixando…",
    testNote: "Modo de teste: use o cartão 4242 4242 4242 4242, qualquer data e CVC.",
    okDownload: "Download iniciado. Boa leitura.",
    errCheckout: "Não foi possível abrir o pagamento. Tente novamente.",
    errDownload: "Acesso premium inválido ou expirado.",
  },
  fr: {
    overline: "Contenu Premium",
    title: "Thèse complète · Document officiel UCV",
    desc: "La recherche intégrale (tous les chapitres, annexes et références) disponible pour les abonnés.",
    benefits: ["Téléchargement immédiat du document complet", "Accès aux futurs contenus premium", "Soutenez directement l'auteur", "Résiliez quand vous voulez"],
    subscribe: "S'abonner — 9,99 $/mois",
    subscribing: "Ouverture du paiement sécurisé…",
    unlocked: "Abonnement premium actif",
    download: "Télécharger la thèse complète",
    downloading: "Téléchargement…",
    testNote: "Mode test : utilisez la carte 4242 4242 4242 4242, date et CVC quelconques.",
    okDownload: "Téléchargement lancé. Bonne lecture.",
    errCheckout: "Impossible d'ouvrir le paiement. Réessayez.",
    errDownload: "Accès premium invalide ou expiré.",
  },
};

const PremiumGate = () => {
  const { lang } = useLang();
  const T = TXT[lang] ?? TXT.es;
  const [token, setToken] = useState(() => localStorage.getItem("alagrande_premium") || "");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const sync = () => setToken(localStorage.getItem("alagrande_premium") || "");
    window.addEventListener("premium-changed", sync);
    return () => window.removeEventListener("premium-changed", sync);
  }, []);

  const subscribe = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: "premium_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error(T.errCheckout);
      setLoading(false);
    }
  };

  const download = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/premium/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Tesis_UCV_JavierAlagrande.docx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(T.okDownload);
    } catch {
      toast.error(T.errDownload);
      localStorage.removeItem("alagrande_premium");
      setToken("");
    } finally {
      setLoading(false);
    }
  };

  if (token) {
    return (
      <div className="mt-8 border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-6" data-testid="premium-unlocked">
        <p className="flex items-center gap-2.5 font-code text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">
          <Crown className="w-4 h-4" />
          {T.unlocked}
        </p>
        <button
          data-testid="premium-download-button"
          onClick={download}
          disabled={loading}
          className="mt-5 w-full flex items-center justify-center gap-2.5 px-5 py-3.5 bg-[#D4AF37] text-[#0D0D0E] text-xs tracking-[0.18em] uppercase font-semibold hover:bg-[#E5C158] disabled:opacity-70 transition-colors duration-300"
        >
          <Download className="w-4 h-4" />
          {loading ? T.downloading : T.download}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-8 border border-[#D4AF37]/25 bg-[#0D0D0E] p-6" data-testid="premium-gate">
      <p className="flex items-center gap-2.5 font-code text-[11px] tracking-[0.3em] uppercase text-[#C5A059]">
        <Lock className="w-4 h-4" />
        {T.overline}
      </p>
      <h4 className="mt-3 font-display text-xl text-[#F4F1EA]">{T.title}</h4>
      <p className="mt-2 text-sm text-[#A0A0A5] leading-relaxed">{T.desc}</p>
      <ul className="mt-4 space-y-2">
        {T.benefits.map((b, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-[#D8D4C8]">
            <Check className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
            {b}
          </li>
        ))}
      </ul>
      <button
        data-testid="premium-subscribe-button"
        onClick={subscribe}
        disabled={loading}
        className="mt-6 w-full flex items-center justify-center gap-2.5 px-5 py-4 bg-[#D4AF37] text-[#0D0D0E] text-xs tracking-[0.18em] uppercase font-semibold hover:bg-[#E5C158] disabled:opacity-70 transition-colors duration-300"
      >
        <Crown className="w-4 h-4" />
        {loading ? T.subscribing : T.subscribe}
      </button>
      <p className="mt-3 text-center font-code text-[10px] text-[#5a5a5e]">{T.testNote}</p>
    </div>
  );
};

export default PremiumGate;
